import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FiExternalLink, FiMenu } from 'react-icons/fi';
import dashboardStyles from '../AccueilDashboard.module.scss';
import widgetStyles from './Widget.module.scss';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as actions from '../../../services/actions';
import WidgetGraphe from './WidgetGraphe';
import WidgetPlan from './WidgetPlan';
import WidgetTableauDeBord from './WidgetTableauDeBord';
import Popup from '../../UI/PopupVue/Popup';
import AffichageVue from '../../Vue/AffichageVue/AffichageVue';
import SwitchGraphe from '../../Graphe/AffichageGraphe/SwitchGraphe';

const GrapheContenu = connect(
  (state, { graph_id }) => ({
    donneesGraphe: state.graphe.liste_donnees_graphes[graph_id] ?? {},
  }),
  (dispatch) => ({
    reqRecupererDonneesGraphe: (id) =>
      dispatch(actions.reqRecupererDonneesGraphe(id)),
  })
)(({ graph_id, donneesGraphe, reqRecupererDonneesGraphe }) => {
  useEffect(() => {
    if (graph_id) reqRecupererDonneesGraphe(graph_id);
  }, [graph_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!donneesGraphe?.config && !donneesGraphe?.data) {
    return (
      <div style={{ padding: '2rem' }}>{checkMot('chargement_en_cours')}</div>
    );
  }

  return (
    <SwitchGraphe
      graph_id={graph_id}
      donneesGraphe={donneesGraphe}
      configuration={donneesGraphe.config}
      donnees={donneesGraphe.data}
      seuils={donneesGraphe.alarms_levels ?? []}
      switchAfficherModifierIntervalle={() => {}}
      reqRecupererDonneesGraphe={reqRecupererDonneesGraphe}
    />
  );
});

const STORAGE_KEY = 'dashboardSlots';
const CONTENT_SELECTION_KEY = 'dashboardContentSelectionById';
const DEFAULT_SLOTS = ['plan', 'graphe', 'dashboard'];

const getItemId = (widgetKey, item) => {
  if (!item) return null;
  if (widgetKey === 'graphe') return item.graph_id ?? null;
  return item.view_id ?? null;
};

const WIDGET_CONFIG = {
  plan: { Component: WidgetPlan, labelKey: 'Plan' },
  graphe: { Component: WidgetGraphe, labelKey: 'Graphe' },
  dashboard: { Component: WidgetTableauDeBord, labelKey: 'Tableau de bord' },
};

const sanitizeSlots = (slots, maxInstancesByWidget = {}) => {
  if (!Array.isArray(slots)) return null;

  const normalized = DEFAULT_SLOTS.map((defaultId, index) => {
    const slotId = slots[index];
    return WIDGET_CONFIG[slotId] ? slotId : defaultId;
  });

  const counts = {};
  const clamped = normalized.map((slotId) => {
    const maxForWidget = Math.max(1, maxInstancesByWidget[slotId] ?? 1);
    const currentCount = counts[slotId] || 0;
    if (currentCount < maxForWidget) {
      counts[slotId] = currentCount + 1;
      return slotId;
    }

    const replacement = Object.keys(WIDGET_CONFIG).find((widgetId) => {
      const widgetMax = Math.max(1, maxInstancesByWidget[widgetId] ?? 1);
      return (counts[widgetId] || 0) < widgetMax;
    });

    if (replacement) {
      counts[replacement] = (counts[replacement] || 0) + 1;
      return replacement;
    }

    return slotId;
  });

  return clamped.length === 3 ? clamped : null;
};

const WidgetWidgetsLayout = ({
  dashboards = [],
  vuesFavorites = [],
  graphesFavoris = [],
  viewData = null,
  loading = false,
  onDashboardClick,
  onVueClick,
  onGrapheClick,
}) => {
  const dispatch = useDispatch();
  const [slots, setSlots] = useState(DEFAULT_SLOTS);
  const [openMenuSlot, setOpenMenuSlot] = useState(null);
  const [contentSelectionBySlot, setContentSelectionBySlot] = useState({});
  const [popupViewId, setPopupViewId] = useState(null);
  const fermerPopup = () => setPopupViewId(null);
  const [popupGrapheId, setPopupGrapheId] = useState(null);
  const fermerPopupGraphe = () => setPopupGrapheId(null);

  // Carregar favoritos de gráficos no montagem
  useEffect(() => {
    // Garantir que os favoritos sejam carregados quando o componente é montado
    dispatch(actions.reqRecupererFavoriteGraphsV25());
  }, [dispatch]);

  const contentListsByWidget = useMemo(
    () => ({
      plan: vuesFavorites,
      graphe: graphesFavoris,
      dashboard: dashboards,
    }),
    [vuesFavorites, graphesFavoris, dashboards]
  );

  const maxInstancesByWidget = useMemo(
    () => ({
      plan: contentListsByWidget.plan.length,
      graphe: contentListsByWidget.graphe.length,
      dashboard: dashboards.length || (viewData ? 1 : 0),
    }),
    [
      contentListsByWidget.plan.length,
      contentListsByWidget.graphe.length,
      dashboards.length,
      viewData,
    ]
  );

  // Leitura única do localStorage na montagem – não depende de normalizeSlots/maxInstancesByWidget
  // para evitar resets a cada carregamento de dados da API.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Array.isArray(stored) && stored.length === DEFAULT_SLOTS.length) {
        const validSlots = stored.map((s, i) =>
          WIDGET_CONFIG[s] ? s : DEFAULT_SLOTS[i]
        );
        setSlots(validSlots);
      }
      const storedContent = JSON.parse(
        localStorage.getItem(CONTENT_SELECTION_KEY)
      );
      if (
        storedContent &&
        typeof storedContent === 'object' &&
        !Array.isArray(storedContent)
      ) {
        setContentSelectionBySlot(storedContent);
      }
    } catch {
      // ignore storage issues
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
  }, [slots]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (Object.keys(contentSelectionBySlot).length === 0) return;
    localStorage.setItem(
      CONTENT_SELECTION_KEY,
      JSON.stringify(contentSelectionBySlot)
    );
  }, [contentSelectionBySlot]);

  useEffect(() => {
    if (openMenuSlot === null) return;
    const handleClick = (event) => {
      if (event.target.closest('[data-widget-content-menu-root]')) return;
      setOpenMenuSlot(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [openMenuSlot]);

  // Revalida os slots quando o conteúdo disponível muda – sem re-ler o storage.
  // Aguarda que pelo menos um tipo de widget tenha dados antes de revalidar,
  // para não destruir os slots restaurados do localStorage enquanto a API ainda carrega.
  useEffect(() => {
    const hasData = Object.values(maxInstancesByWidget).some((v) => v > 0);
    if (!hasData) return;
    setSlots((prev) => {
      const normalized = sanitizeSlots(prev, maxInstancesByWidget);
      if (!normalized) return prev;
      const hasChanged = normalized.some((s, i) => s !== prev[i]);
      return hasChanged ? normalized : prev;
    });
  }, [maxInstancesByWidget]);

  useEffect(() => {
    const hasData = Object.values(contentListsByWidget).some(
      (list) => list.length > 0
    );
    if (!hasData) return;
    setContentSelectionBySlot((prev) => {
      const next = {};
      const usedIdsByType = {
        dashboard: new Set(),
        plan: new Set(),
        graphe: new Set(),
      };

      slots.forEach((slotWidget, slotIndex) => {
        const list = contentListsByWidget[slotWidget] || [];
        if (list.length === 0) return;

        const storedId = prev[slotIndex];
        if (storedId != null) {
          const item = list.find(
            (it) => String(getItemId(slotWidget, it)) === String(storedId)
          );
          if (item && !usedIdsByType[slotWidget].has(String(storedId))) {
            usedIdsByType[slotWidget].add(String(storedId));
            next[slotIndex] = storedId;
            return;
          }
        }

        const firstAvailable = list.find((it) => {
          const id = getItemId(slotWidget, it);
          return id != null && !usedIdsByType[slotWidget].has(String(id));
        });
        if (firstAvailable) {
          const id = getItemId(slotWidget, firstAvailable);
          usedIdsByType[slotWidget].add(String(id));
          next[slotIndex] = id;
        }
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      const isSameLength = prevKeys.length === nextKeys.length;
      const isSameValues =
        isSameLength &&
        nextKeys.every((key) => String(prev[key]) === String(next[key]));

      return isSameValues ? prev : next;
    });
  }, [slots, contentListsByWidget]);

  const areaConfig = useMemo(
    () => [
      { className: dashboardStyles.planArea, sizeVariant: 'large' },
      { className: dashboardStyles.grapheArea, sizeVariant: 'medium' },
      { className: dashboardStyles.tableauArea, sizeVariant: 'large' },
    ],
    []
  );

  const propsByWidget = useMemo(
    () => ({
      graphe: { graphesFavoris, onGrapheClick, loading },
      plan: { vuesFavorites, onVueClick, loading },
      dashboard: {
        dashboards,
        vuesFavorites,
        graphesFavoris,
        viewData,
        loading,
        onDashboardClick,
        onVueClick,
        onGrapheClick,
        availableWidgets: ['dashboard'],
        initialWidget: 'dashboard',
      },
    }),
    [
      dashboards,
      vuesFavorites,
      graphesFavoris,
      viewData,
      loading,
      onDashboardClick,
      onVueClick,
      onGrapheClick,
    ]
  );

  const handleSlotChange = useCallback(
    (slotIndex, newWidget) => {
      setSlots((prev) => {
        if (prev[slotIndex] === newWidget) return prev;
        const countForNewWidget = prev.filter(
          (slotId) => slotId === newWidget
        ).length;
        const currentIsSameType = prev[slotIndex] === newWidget;
        const maxForNewWidget = Math.max(
          1,
          maxInstancesByWidget[newWidget] ?? 1
        );

        if (!currentIsSameType && countForNewWidget >= maxForNewWidget) {
          return prev;
        }

        const next = [...prev];
        next[slotIndex] = newWidget;
        return sanitizeSlots(next, maxInstancesByWidget) || next;
      });
    },
    [maxInstancesByWidget]
  );

  const handleContentChange = useCallback(
    (slotIndex, contentIndex) => {
      const widgetKey = slots[slotIndex];
      const list = contentListsByWidget[widgetKey] || [];
      const item = list[contentIndex];
      const itemId = getItemId(widgetKey, item);
      setContentSelectionBySlot((prev) => ({
        ...prev,
        [slotIndex]: itemId ?? contentIndex,
      }));
      setOpenMenuSlot(null);
    },
    [slots, contentListsByWidget]
  );

  const slotTypeCounts = useMemo(
    () =>
      slots.reduce((acc, slotId) => {
        acc[slotId] = (acc[slotId] || 0) + 1;
        return acc;
      }, {}),
    [slots]
  );

  const widgetOptions = useMemo(
    () =>
      Object.entries(WIDGET_CONFIG).map(([id, cfg]) => ({
        value: id,
        label: checkMot(cfg.labelKey),
      })),
    []
  );

  const getContentLabel = useCallback((widgetKey, item, index) => {
    if (widgetKey === 'graphe') {
      return item?.graph_name || `${checkMot('graphe')} ${index + 1}`;
    }

    if (widgetKey === 'plan') {
      return item?.view_name || `${checkMot('plan')} ${index + 1}`;
    }

    return (
      item?.view_name ||
      item?.dashboard_name ||
      `${checkMot('tableau_de_bord')} ${index + 1}`
    );
  }, []);

  const renderWidget = (widgetKey, slotIndex) => {
    const config = WIDGET_CONFIG[widgetKey];
    if (!config) return null;
    const Component = config.Component;
    const extraProps = propsByWidget[widgetKey] || {};
    const area = areaConfig[slotIndex] || {};
    const currentContentList = contentListsByWidget[widgetKey] || [];
    const storedId = contentSelectionBySlot[slotIndex];
    const resolvedIndex =
      storedId != null
        ? currentContentList.findIndex(
            (it) => String(getItemId(widgetKey, it)) === String(storedId)
          )
        : -1;
    const finalIndex = resolvedIndex >= 0 ? resolvedIndex : 0;

    const usedIdsForType = new Set(
      slots
        .map((slotType, idx) =>
          slotType === widgetKey && idx !== slotIndex
            ? String(contentSelectionBySlot[idx])
            : null
        )
        .filter((id) => id != null && id !== 'null' && id !== 'undefined')
    );

    const contentOptions = currentContentList.map((item, index) => {
      const itemId = String(getItemId(widgetKey, item) ?? index);
      return {
        value: index,
        label: getContentLabel(widgetKey, item, index),
        disabled: itemId !== String(storedId) && usedIdsForType.has(itemId),
      };
    });

    const dashboardItem =
      widgetKey === 'dashboard' ? dashboards[finalIndex] || null : null;
    const vueItem =
      widgetKey === 'plan' ? vuesFavorites[finalIndex] || null : null;
    const grapheItem =
      widgetKey === 'graphe' ? graphesFavoris[finalIndex] || null : null;

    const externalLinkHref =
      widgetKey === 'graphe' && grapheItem?.graph_id != null
        ? `https://argos-v19.symf.itmsol.fr/export_impression_ecran-graphe-${grapheItem.graph_id}`
        : widgetKey === 'plan' && vueItem?.view_id != null
          ? `https://argos-v19.symf.itmsol.fr/export_impression_ecran-visualisation-${vueItem.view_id}`
          : widgetKey === 'dashboard' && dashboardItem?.view_id != null
            ? `https://argos-v19.symf.itmsol.fr/export_impression_ecran-visualisation-${dashboardItem.view_id}`
            : null;

    const headerContent = (
      <div className={widgetStyles.dashboardWidgetHeader}>
        <div className={widgetStyles.dashboardHeaderControls}>
          <select
            className={widgetStyles.dashboardWidgetSelect}
            value={widgetKey}
            onChange={(event) =>
              handleSlotChange(slotIndex, event.target.value)
            }
            aria-label={checkMot(config.labelKey)}
          >
            {widgetOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={
                  option.value !== widgetKey &&
                  (slotTypeCounts[option.value] || 0) >=
                    Math.max(1, maxInstancesByWidget[option.value] ?? 1)
                }
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div
          className={widgetStyles.dashboardWidgetMenuWrapper}
          data-widget-content-menu-root
        >
          {externalLinkHref && (
            <a
              href={externalLinkHref}
              className={widgetStyles.dashboardWidgetMenuButton}
              onClick={(event) => {
                event.preventDefault();
                if (widgetKey === 'graphe') {
                  if (grapheItem?.graph_id)
                    setPopupGrapheId(grapheItem.graph_id);
                } else {
                  const viewId =
                    widgetKey === 'plan'
                      ? vueItem?.view_id
                      : dashboardItem?.view_id;
                  if (viewId) setPopupViewId(viewId);
                }
              }}
              title={checkMot('voir_plus')}
              disabled={!externalLinkHref}
            >
              <FiExternalLink size={18} />
            </a>
          )}
          <button
            type="button"
            className={widgetStyles.dashboardWidgetMenuButton}
            onClick={() =>
              setOpenMenuSlot((prev) => (prev === slotIndex ? null : slotIndex))
            }
            aria-haspopup="menu"
            aria-expanded={openMenuSlot === slotIndex}
            disabled={contentOptions.length === 0}
          >
            <FiMenu size={18} />
          </button>
          {openMenuSlot === slotIndex && (
            <div className={widgetStyles.dashboardWidgetMenu} role="menu">
              {contentOptions.length > 0 ? (
                contentOptions.map((option) => (
                  <button
                    key={`${widgetKey}-${option.value}`}
                    type="button"
                    role="menuitem"
                    className={widgetStyles.dashboardWidgetMenuItem}
                    onClick={() => handleContentChange(slotIndex, option.value)}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <div className={widgetStyles.dashboardWidgetMenuEmpty}>
                  Aucun contenu disponible
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );

    const widgetProps = {
      ...extraProps,
      ...(widgetKey === 'dashboard'
        ? { dashboards: dashboardItem ? [dashboardItem] : [] }
        : {}),
      ...(widgetKey === 'plan'
        ? { vuesFavorites: vueItem ? [vueItem] : [] }
        : {}),
      ...(widgetKey === 'graphe'
        ? { graphesFavoris: grapheItem ? [grapheItem] : [] }
        : {}),
      ...(widgetKey === 'dashboard' ? { sizeVariant: area.sizeVariant } : {}),
      headerContent,
      showHeader: false,
    };

    return <Component key={`${widgetKey}-${slotIndex}`} {...widgetProps} />;
  };

  return (
    <>
      {slots.map((widgetKey, slotIndex) => {
        const area = areaConfig[slotIndex] || {};
        return (
          <div
            key={`slot-${slotIndex}`}
            className={[dashboardStyles.area, area.className].join(' ')}
          >
            {renderWidget(widgetKey, slotIndex)}
          </div>
        );
      })}
      <Popup
        sansBouton={true}
        eventALaFermeture={fermerPopup}
        showModal={popupViewId !== null}
      >
        {popupViewId && (
          <AffichageVue
            view_id={popupViewId}
            mapContainerId={`mapPopupLayout-${popupViewId}`}
            uniciteVue={`layout-pop-${popupViewId}`}
            skipStorePurge
          />
        )}
      </Popup>
      <Popup
        sansBouton={true}
        eventALaFermeture={fermerPopupGraphe}
        showModal={popupGrapheId !== null}
      >
        {popupGrapheId && <GrapheContenu graph_id={popupGrapheId} />}
      </Popup>
    </>
  );
};

export default WidgetWidgetsLayout;
